<template>
  <Modal :open="open" title="Meu perfil" size="lg" variant="gray" fixed-height-mobile :close-on-overlay-click="false" @close="emit('close')">
    <form id="profile-form" class="modal-form-gray" novalidate @submit.prevent="save">
      <!-- Mobile: etapas (mesmo padrão dos cadastros de usuário/serviço).
           Desktop: abas. As duas navegam pelo mesmo activeTab. -->
      <div class="md:hidden mb-4">
        <div class="flex items-center gap-2 mb-4">
          <div v-for="(tab, i) in tabs" :key="tab.id"
            class="flex-1 h-1 rounded-full transition-all duration-500"
            :class="i <= stepIndex ? 'bg-primary-500' : 'bg-zinc-200 dark:bg-zinc-800'" />
        </div>
        <div class="flex items-center gap-3">
          <button v-if="stepIndex > 0" type="button" aria-label="Etapa anterior" @click="goToStep(stepIndex - 1)"
            class="w-8 h-8 flex items-center justify-center rounded-full bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-300 hover:bg-zinc-200 dark:hover:bg-zinc-700 transition-colors flex-shrink-0">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div>
            <p class="text-[11px] font-semibold text-primary-500 uppercase tracking-wider">Passo {{ stepIndex + 1 }} de {{ tabs.length }}</p>
            <p class="text-base font-bold text-zinc-900 dark:text-white">{{ tabs[stepIndex].label }}</p>
          </div>
        </div>
      </div>
      <TabPills v-model="activeTab" :tabs="tabs" class="hidden md:block mb-4" />

      <!-- Altura fixa no desktop (a do maior conteúdo, o expediente): trocar
           de aba não faz o modal crescer/encolher; o que passar rola aqui
           dentro. No mobile o modal já tem altura fixa (fixed-height-mobile).
           p-1/-m-1: espaço pro anel de foco dos campos não ser cortado. -->
      <div class="md:h-[26rem] md:overflow-y-auto p-1 -m-1">
        <div v-if="loading" class="flex items-center justify-center gap-2 py-12 text-sm text-slate-400">
          <div class="loading-spinner w-4 h-4" /> Carregando seu cadastro...
        </div>

        <template v-else>
        <!-- Dados pessoais -->
        <div v-show="activeTab === 'dados'" class="rounded-xl border border-slate-200 dark:border-zinc-700 p-4 space-y-4">
          <div class="form-group">
            <label class="form-label">Nome *</label>
            <input v-model="form.name" type="text" class="form-input" placeholder="Seu nome" />
          </div>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div class="form-group">
              <label class="form-label">E-mail *</label>
              <input v-model="form.email" type="email" class="form-input" placeholder="seu@email.com" />
            </div>
            <div class="form-group">
              <label class="form-label">Telefone</label>
              <input :value="form.phone" type="tel" class="form-input" placeholder="(00) 00000-0000" maxlength="15"
                @input="e => { form.phone = applyPhoneMask(e.target.value); e.target.value = form.phone; phoneError = '' }" />
              <p v-if="phoneError" class="mt-1 text-xs text-danger-500">{{ phoneError }}</p>
            </div>
          </div>
          <!-- Definidos pelo administrador — só consulta -->
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div class="form-group">
              <label class="form-label">Perfil</label>
              <input :value="roleLabel" type="text" disabled class="form-input opacity-60 cursor-not-allowed" />
            </div>
            <div class="form-group">
              <label class="form-label">Estabelecimento</label>
              <input :value="profile?.establishment_name || '—'" type="text" disabled class="form-input opacity-60 cursor-not-allowed" />
            </div>
          </div>
        </div>

        <!-- Endereço -->
        <div v-show="activeTab === 'endereco'" class="rounded-xl border border-slate-200 dark:border-zinc-700 p-4 space-y-4">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div class="form-group">
              <label class="form-label">CEP</label>
              <div class="relative">
                <input
                  :value="form.zip_code"
                  type="text" inputmode="numeric" maxlength="9" class="form-input pr-8" placeholder="00000-000"
                  @input="e => { form.zip_code = maskCep(e.target.value); e.target.value = form.zip_code }"
                  @blur="lookupCep(form.zip_code, d => { form.address = d.address || form.address; form.neighborhood = d.neighborhood; form.city = d.city; form.state = d.state; form.zip_code = d.zip_code })"
                />
                <div v-if="cepLoading" class="absolute right-2.5 top-1/2 -translate-y-1/2">
                  <div class="loading-spinner w-3.5 h-3.5" />
                </div>
              </div>
              <p v-if="cepError" class="mt-1 text-xs text-danger-500">{{ cepError }}</p>
            </div>
            <div class="form-group">
              <label class="form-label">Estado (UF)</label>
              <input v-model="form.state" type="text" class="form-input" placeholder="UF" maxlength="2" @input="form.state = form.state.toUpperCase()" />
            </div>
          </div>
          <div class="flex flex-col sm:flex-row gap-4">
            <div class="form-group flex-1">
              <label class="form-label">Rua</label>
              <input v-model="form.address" type="text" class="form-input" placeholder="Rua" />
            </div>
            <div class="form-group sm:w-28 sm:shrink-0">
              <label class="form-label">Número</label>
              <input v-model="form.address_number" type="text" class="form-input" placeholder="Nº" />
            </div>
          </div>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div class="form-group">
              <label class="form-label">Bairro</label>
              <input v-model="form.neighborhood" type="text" class="form-input" placeholder="Bairro" />
            </div>
            <div class="form-group">
              <label class="form-label">Cidade</label>
              <input v-model="form.city" type="text" class="form-input" placeholder="Cidade" />
            </div>
          </div>
        </div>

        <!-- Expediente — nas unidades a que o admin vinculou o usuário -->
        <div v-show="activeTab === 'expediente'" class="rounded-xl border border-slate-200 dark:border-zinc-700 p-4">
          <p class="text-xs text-slate-400 dark:text-zinc-500 mb-3">
            As unidades em que você atende são definidas pelo administrador. Aqui você ajusta os dias e horários, sempre dentro do horário de funcionamento de cada unidade.
          </p>
          <div v-for="branch in branches" :key="branch.id" class="mt-4 first:mt-0">
            <p class="text-sm font-semibold text-slate-800 dark:text-zinc-100">
              {{ branch.name }}
              <span v-if="showEstablishmentName" class="font-normal text-slate-400 dark:text-zinc-500"> · {{ branch.establishment_name }}</span>
            </p>
            <p v-if="!branch.opening_hours.length" class="mt-1 text-xs text-slate-400 dark:text-zinc-500 italic">
              Esta unidade não possui horários cadastrados.
            </p>
            <div v-else class="mt-2 space-y-1.5">
              <div v-for="opening in branch.opening_hours" :key="opening.day_of_week"
                class="rounded-lg px-2.5 py-2 transition-colors"
                :class="slot(branch.id, opening.day_of_week).enabled ? 'bg-slate-50 dark:bg-zinc-800/40' : ''">
                <div class="flex items-center gap-2">
                  <label class="flex items-center gap-2 flex-1 min-w-0 cursor-pointer select-none">
                    <input type="checkbox" v-model="slot(branch.id, opening.day_of_week).enabled"
                      class="w-4 h-4 rounded accent-primary-500 shrink-0" />
                    <span class="text-sm font-semibold truncate"
                      :class="slot(branch.id, opening.day_of_week).enabled ? 'text-slate-800 dark:text-zinc-100' : 'text-slate-400 dark:text-zinc-500'">
                      {{ DAY_NAMES[opening.day_of_week] }}
                    </span>
                  </label>
                  <span class="shrink-0 text-[11px] text-slate-400 dark:text-zinc-500 tabular-nums whitespace-nowrap">
                    Unidade: {{ toHHMM(opening.open_time) }}–{{ toHHMM(opening.close_time) }}
                  </span>
                </div>
                <div class="flex items-center gap-2 mt-2 pl-6">
                  <template v-if="slot(branch.id, opening.day_of_week).enabled">
                    <div class="w-[6.25rem] shrink-0" aria-label="Início do expediente">
                      <SelectPicker
                        v-model="slot(branch.id, opening.day_of_week).start_time"
                        :options="timeOptions(opening.open_time, slot(branch.id, opening.day_of_week).end_time, slot(branch.id, opening.day_of_week).start_time)"
                        placeholder="Início" variant="gray" input-class="!h-9 !py-0 !pl-3 !pr-2 tabular-nums"
                      />
                    </div>
                    <span class="text-xs text-slate-400 dark:text-zinc-500 shrink-0">às</span>
                    <div class="w-[6.25rem] shrink-0" aria-label="Fim do expediente">
                      <SelectPicker
                        v-model="slot(branch.id, opening.day_of_week).end_time"
                        :options="timeOptions(slot(branch.id, opening.day_of_week).start_time, opening.close_time, slot(branch.id, opening.day_of_week).end_time)"
                        placeholder="Fim" variant="gray" input-class="!h-9 !py-0 !pl-3 !pr-2 tabular-nums"
                      />
                    </div>
                  </template>
                  <span v-else class="text-xs text-slate-400 dark:text-zinc-500 italic">Não trabalha</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Senha -->
        <div v-show="activeTab === 'senha'" class="rounded-xl border border-slate-200 dark:border-zinc-700 p-4">
          <div class="form-group">
            <label class="form-label">Nova senha <span class="text-slate-400 font-normal">(deixe em branco para manter)</span></label>
            <input v-model="form.password" type="password" class="form-input" placeholder="Nova senha" minlength="8" maxlength="16" autocomplete="new-password" />
            <PasswordRequirements v-if="form.password" :password="form.password" />
          </div>
        </div>

        </template>
      </div>

      <p v-if="error" class="text-xs text-danger-500 dark:text-danger-400 mt-3">{{ error }}</p>
    </form>

    <template #footer>
      <!-- Mobile: sem "Cancelar" (o X do cabeçalho fecha) — Salvar de
           qualquer etapa + Próximo, como na edição de usuário. -->
      <div class="md:hidden flex items-center gap-2">
        <button type="button" class="flex-1 h-11 text-sm" :class="isLastStep ? 'btn-primary' : 'btn-secondary'"
          :disabled="saving || loading || !profile" @click="save">
          <span v-if="saving" class="loading-spinner w-4 h-4 mr-1" />
          Salvar
        </button>
        <button v-if="!isLastStep" type="button" class="btn-primary flex-1 h-11 text-sm" :disabled="loading || !profile" @click="nextStep">
          Próximo
        </button>
      </div>
      <!-- Desktop -->
      <div class="hidden md:flex justify-end gap-2">
        <button type="button" class="btn-secondary px-4 py-2 text-sm" @click="emit('close')">Cancelar</button>
        <button type="button" class="btn-primary px-4 py-2 text-sm" :disabled="saving || loading || !profile" @click="save">
          <span v-if="saving" class="loading-spinner w-3.5 h-3.5 mr-1" />
          Salvar
        </button>
      </div>
    </template>
  </Modal>
</template>

<script setup>
// Modal "Meu perfil" (menu do usuário, TopBar.vue) para a equipe do
// estabelecimento: todos os dados do cadastro, com edição dos dados
// pessoais, da senha e do expediente nas unidades em que atende. Perfil,
// estabelecimento e unidades só o admin muda (Configurações > Usuários) —
// o backend descarta esses campos na auto-edição (ver PUT /users/:id).
import { ref, reactive, computed, watch } from 'vue'
import apiClient from '../../api/client.js'
import { useAuthStore } from '../../stores/auth'
import { useToast } from '../../composables/useToast'
import { useCep } from '../../composables/useCep'
import { composeAddress, splitAddressNumber } from '../../utils/addressHelpers'
import { applyPhoneMask } from '../../utils/formatters'
import { isPasswordValid, PASSWORD_REQUIREMENTS_MESSAGE } from '../../utils/passwordPolicy'
import { timeOptions, toHHMM } from '../../utils/timeOptions'
import { getMessages } from '../../i18n/index.js'
import Modal from '../ui/Modal.vue'
import TabPills from '../common/TabPills.vue'
import SelectPicker from '../common/SelectPicker.vue'
import PasswordRequirements from '../common/PasswordRequirements.vue'

const props = defineProps({
  open: { type: Boolean, default: false },
})
const emit = defineEmits(['close'])

const authStore = useAuthStore()
const { success, handleApiError } = useToast()
const { cepLoading, cepError, maskCep, lookupCep } = useCep()

const DAY_NAMES = ['Domingo', 'Segunda-feira', 'Terça-feira', 'Quarta-feira', 'Quinta-feira', 'Sexta-feira', 'Sábado']

const loading = ref(false)
const saving = ref(false)
const error = ref('')
const phoneError = ref('')
const profile = ref(null)
const activeTab = ref('dados')

const emptyForm = () => ({
  name: '', email: '', phone: '', password: '',
  zip_code: '', state: '', address: '', address_number: '', neighborhood: '', city: '',
})
const form = reactive(emptyForm())
// schedule[branchId][day] = { enabled, start_time, end_time }
const schedule = reactive({})

const branches = computed(() => profile.value?.branches || [])

const tabs = computed(() => [
  { id: 'dados', label: 'Dados pessoais' },
  { id: 'endereco', label: 'Endereço' },
  ...(branches.value.length ? [{ id: 'expediente', label: 'Expediente' }] : []),
  { id: 'senha', label: 'Senha' },
])

// Etapa atual (mobile) = aba ativa (desktop).
const stepIndex = computed(() => Math.max(0, tabs.value.findIndex((t) => t.id === activeTab.value)))
const isLastStep = computed(() => stepIndex.value >= tabs.value.length - 1)
const goToStep = (index) => {
  activeTab.value = tabs.value[Math.max(0, Math.min(index, tabs.value.length - 1))].id
}

// Campos da etapa Dados pessoais. Validação aqui, e não pelo navegador
// (o form é novalidate): numa etapa/aba escondida o navegador não consegue
// mostrar o aviso do campo e o salvamento só "não acontece".
const validatePersonalData = () => {
  phoneError.value = ''
  let message = ''
  if (!form.name.trim() || form.name.trim().length < 2) message = 'Informe seu nome.'
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) message = 'Informe um e-mail válido.'
  else {
    const phoneDigits = form.phone.replace(/\D/g, '')
    if (phoneDigits && phoneDigits.length < 11) phoneError.value = 'O telefone deve ter 11 dígitos.'
  }
  error.value = message
  if (message || phoneError.value) {
    activeTab.value = 'dados'
    return false
  }
  return true
}

const nextStep = () => {
  if (activeTab.value === 'dados' && !validatePersonalData()) return
  error.value = ''
  goToStep(stepIndex.value + 1)
}

const roleLabel = computed(() => getMessages('pt-BR').roles?.[profile.value?.role] || profile.value?.role || '—')

// Mais de um estabelecimento: o nome da unidade sozinho pode ser ambíguo.
const showEstablishmentName = computed(() =>
  new Set(branches.value.map((b) => b.establishment_name)).size > 1)

const slot = (branchId, day) => schedule[branchId][day]

const fillFromProfile = (data) => {
  const parsedAddress = splitAddressNumber(data.address)
  Object.assign(form, emptyForm(), {
    name: data.name || '',
    email: data.email || '',
    phone: data.phone ? applyPhoneMask(data.phone) : '',
    zip_code: data.zip_code || '',
    state: data.state || '',
    address: parsedAddress.address,
    address_number: parsedAddress.address_number,
    neighborhood: data.neighborhood || '',
    city: data.city || '',
  })
  for (const key of Object.keys(schedule)) delete schedule[key]
  for (const branch of data.branches || []) {
    schedule[branch.id] = {}
    for (const opening of branch.opening_hours) {
      const saved = branch.hours.find((h) => h.day_of_week === opening.day_of_week)
      schedule[branch.id][opening.day_of_week] = {
        enabled: !!saved,
        start_time: toHHMM(saved?.start_time || opening.open_time),
        end_time: toHHMM(saved?.end_time || opening.close_time),
      }
    }
  }
}

const load = async () => {
  loading.value = true
  error.value = ''
  phoneError.value = ''
  activeTab.value = 'dados'
  try {
    const { data } = await apiClient.get('/users/profile')
    profile.value = data
    fillFromProfile(data)
  } catch (err) {
    profile.value = null
    handleApiError(err, 'Erro ao carregar seu cadastro')
    emit('close')
  } finally {
    loading.value = false
  }
}

watch(() => props.open, (isOpen) => { if (isOpen) load() })

const buildBranchHours = () => {
  const result = []
  for (const [branchId, days] of Object.entries(schedule)) {
    for (const [day, s] of Object.entries(days)) {
      if (s.enabled) {
        result.push({ branch_id: Number(branchId), day_of_week: Number(day), start_time: s.start_time, end_time: s.end_time })
      }
    }
  }
  return result
}

const save = async () => {
  if (saving.value || !profile.value) return
  if (!validatePersonalData()) return
  if (form.password && !isPasswordValid(form.password)) {
    error.value = PASSWORD_REQUIREMENTS_MESSAGE
    activeTab.value = 'senha'
    return
  }

  saving.value = true
  try {
    const payload = {
      name: form.name,
      email: form.email,
      phone: form.phone || null,
      zip_code: form.zip_code || null,
      state: form.state || null,
      address: composeAddress(form.address, form.address_number) || null,
      neighborhood: form.neighborhood || null,
      city: form.city || null,
    }
    if (form.password) payload.password = form.password
    if (branches.value.length) payload.user_branch_hours = buildBranchHours()

    const res = await apiClient.put(`/users/${profile.value.id}`, payload)
    if (authStore.user) {
      authStore.user.name = res.data.data?.name || form.name
      authStore.user.email = res.data.data?.email || form.email
    }
    success('Perfil atualizado', 'Suas informações foram salvas com sucesso.')
    emit('close')
  } catch (err) {
    error.value = err?.response?.data?.error || 'Erro ao salvar perfil'
  } finally {
    saving.value = false
  }
}
</script>
